import { isEmpty } from 'lodash'
import { EVENTS } from '~/common/constants'
import { action, computed, event, observable, store } from '~/common/mobx.decorator'
import { authStore } from '~/stores'

@store()
class NotifyStore {
	@observable messages = []
	@observable doneMessages = {}

	@observable dialogs = []
	@observable doneDialogs = {}
	@observable isOpenNotification = false
	@observable isErrorInitOneSignal = false
	@computed
	get waitMessages() {
		return this.messages.filter((message) => !this.doneMessages[message.key])
	}

	@computed
	get waitDialogs() {
		return this.dialogs.filter((dialog) => !this.doneDialogs[dialog.key])
	}

	@computed
	get signal() {
		return window.OneSignalDeferred || window.OneSignal
	}

	@action
	open = async (message, options) => {
		const data = {
			key: `${Date.now()}_${Math.random()}`,
			message,
			options,
		}

		if (options.showDialog) {
			this.dialogs.push(data)

			return data.key
		}
		if (options?.onlyWebPush) {
			return
		}

		this.messages.push(data)
		return data.key
	}

	@action
	success = async (message, options) => {
		return this.open(message, { ...options, variant: 'success' })
	}

	@action
	error = async (message, options) => {
		return this.open(message, { ...options, variant: 'error', icon: 'error' })
	}

	@action
	info = async (message, options) => {
		return this.open(message, { ...options, variant: 'info' })
	}

	@action
	warning = async (message, options) => {
		return this.open(message, { ...options, variant: 'warning' })
	}

	@action
	closeMessage = async (key) => {
		this.doneMessages[key] = true
	}

	@action
	closeDialog = async (key) => {
		this.doneDialogs[key] = true
	}

	@event(EVENTS.notifyStore.fireError)
	handleError({ payload }) {
		this.error(payload?.message)
	}

	@action
	initOneSignal = async () => {
		if (window.location.hostname === 'localhost') {
			console.warn('OneSignal: skipping init on localhost (domain-restricted)')
			return
		}
		if (!isEmpty(process.env.REACT_APP_COMPANY_ID)) {
			try {
				window.OneSignalDeferred = window.OneSignalDeferred || []
				await new Promise((resolve, reject) => {
					window.OneSignalDeferred.push((OneSignal) => {
						OneSignal.init({
							appId: process.env.REACT_APP_ONESIGNAL_APP_ID,
							allowLocalhostAsSecureOrigin: true,
							serviceWorkerParam: { scope: '/push/onesignal/' },
							serviceWorkerPath: '/push/onesignal/OneSignalSDKWorker.js',
						})
							.then(() => {
								this.isErrorInitOneSignal = false
								resolve()
							})
							.catch((error) => {
								console.error(error)
								this.isErrorInitOneSignal = true
								reject(error)
							})
					})
				})
			} catch (error) {
				console.error(error)
				this.isErrorInitOneSignal = true
			}
		}
	}

	@action
	sendInfo = async () => {
		const { profile } = authStore
		if (isEmpty(process.env.REACT_APP_ONESIGNAL_APP_ID)) {
			return
		}

		window.OneSignalDeferred = window.OneSignalDeferred || []
		window.OneSignalDeferred.push(async (OneSignal) => {
			OneSignal.Notifications.addEventListener('click', (event) => {
				this.isOpenNotification = true
				console.log('Notification opened:', event)
			})

			if (profile?.id) {
				await OneSignal.login(String(profile.id))
			}

			const isOptedIn = OneSignal.User.PushSubscription.optedIn

			if (isOptedIn) {
				await OneSignal.User.PushSubscription.optIn()
			} else {
				await OneSignal.Notifications.requestPermission()
			}

			try {
				await OneSignal.User.addTags({
					worker: profile?.id || '',
				})
				console.warn('tagsSent')
			} catch (e) {
				console.warn('e: ', e)
			}
		})
	}

	@action
	unInitOneSignal = async () => {
		window.OneSignalDeferred = window.OneSignalDeferred || []
		window.OneSignalDeferred.push(async (OneSignal) => {
			console.log('🚀 ~ NotifyStore ~ unInitOneSignal')
			await OneSignal.User.PushSubscription.optOut()
			await OneSignal.logout()
		})
	}
}

export const notifyStore = new NotifyStore()
